// src/components/work/UnscheduledTray.jsx
// Unscheduled Tray — drag items without a schedule onto the calendar
// -----------------------------------------------------------------------------
// Optimized for compact layout + FullCalendar external drag support:
//  • Whole card is draggable (no always-visible drag handle).
//  • Actions are icon-only (quick Schedule + Details).
//  • Left color bar by type; priority/client chips stay compact.
//  • Drag payload uses custom MIME + JSON fallback and sets a clean drag image.
//  • Registers cards with @fullcalendar/interaction Draggable so the calendar accepts drops.
//
// Drag payload (HTML5 DataTransfer):
//   MIME: 'application/x-hypercrm-work'
//   JSON: { kind:'work', id, type, title, clientId?, defaultDurationMinutes }

import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';
import { Draggable } from '@fullcalendar/interaction';
import { useWorkList, WORK_KEYS } from '../../hooks/useWorkQueries';

const cx = (...args) =>
  args
    .flatMap((a) =>
      typeof a === 'string'
        ? a
        : a && typeof a === 'object'
        ? Object.keys(a).filter((k) => a[k])
        : []
    )
    .join(' ')
    .trim();

/* -------------------------------- icons -------------------------------- */

const ChevronIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...p}>
    <path d="M8 10l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const RefreshIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...p}>
    <path d="M20 12a8 8 0 1 1-2.343-5.657M20 4v6h-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const CalendarPlusIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...p}>
    <path d="M7 2v4M17 2v4M3 10h18M12 14v6M9 17h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const DotsIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...p}>
    <circle cx="12" cy="6" r="1.6" fill="currentColor" />
    <circle cx="12" cy="12" r="1.6" fill="currentColor" />
    <circle cx="12" cy="18" r="1.6" fill="currentColor" />
  </svg>
);

/* ------------------------------ small helpers ----------------------------- */

const DEFAULT_DURATIONS = { task: 60, job: 90, appointment: 60 }; // minutes

const PRIORITY_BADGE = {
  low: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
  normal: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-200',
  high: 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200',
  urgent: 'bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-200',
};

const TYPE_ACCENT = {
  task: 'bg-sky-500',
  job: 'bg-amber-500',
  appointment: 'bg-violet-500',
};

const TYPE_PILL = {
  task: 'bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-200 dark:ring-sky-900',
  job: 'bg-amber-50 text-amber-800 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:ring-amber-900',
  appointment:
    'bg-violet-50 text-violet-800 ring-violet-200 dark:bg-violet-950/40 dark:text-violet-200 dark:ring-violet-900',
};

function prettyType(t) {
  if (t === 'job') return 'Job';
  if (t === 'appointment') return 'Appointment';
  return 'Task';
}

// lightweight drag image (canvas with colored bar + title)
function setCleanDragImage(dt, title = 'Work', type = 'task') {
  try {
    const scale = window.devicePixelRatio || 1;
    const w = 180,
      h = 36;
    const c = document.createElement('canvas');
    c.width = w * scale;
    c.height = h * scale;
    const ctx = c.getContext('2d');
    ctx.scale(scale, scale);
    // card
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#cbd5e1'; // slate-300
    ctx.lineWidth = 1;
    ctx.fillRect(0, 0, w, h);
    ctx.strokeRect(0.5, 0.5, w - 1, h - 1);
    // left bar
    ctx.fillStyle = type === 'job' ? '#f59e0b' : type === 'appointment' ? '#8b5cf6' : '#0ea5e9';
    ctx.fillRect(0, 0, 6, h);
    // text
    ctx.fillStyle = '#0f172a'; // slate-900
    ctx.font = '12px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
    ctx.textBaseline = 'middle';
    const text = String(title || '').slice(0, 32);
    ctx.fillText(text, 10, h / 2);
    const img = new Image();
    img.src = c.toDataURL();
    const proxy = document.createElement('div');
    proxy.style.position = 'fixed';
    proxy.style.top = '-1000px';
    proxy.style.left = '-1000px';
    proxy.appendChild(img);
    document.body.appendChild(proxy);
    dt.setDragImage(proxy, 10, 14);
    setTimeout(() => proxy.remove(), 0);
  } catch {
    /* noop */
  }
}

/* ------------------------------ main component ---------------------------- */

export default function UnscheduledTray({
  // Parent-controlled data (optional)
  items: itemsProp,
  isLoading: isLoadingProp,
  isFetching: isFetchingProp,
  error: errorProp,
  onRefresh,

  // Actions
  onOpenDetails,
  // onCreateSchedule / onScheduleNow are deprecated in favor of opening the drawer
  onCreateSchedule, /* deprecated */
  onScheduleNow,    /* deprecated */
  dragEnabled = true,

  // Defaults & UI
  defaultDurations = DEFAULT_DURATIONS,
  initialCollapsed = false,
  collapsed: collapsedProp,
  onCollapsedChange,
  className,
}) {
  const controlled = typeof collapsedProp === 'boolean';
  const [collapsedState, setCollapsedState] = useState(initialCollapsed);
  const collapsed = controlled ? collapsedProp : collapsedState;

  const [q, setQ] = useState('');

  // Internal fetch (fallback only). Keeps tray reusable standalone.
  const params = useMemo(() => ({ limit: 200, sort: 'createdAt:desc', onlyUnscheduled: 1 }), []);
  const {
    items: hookItems = [],
    isFetching: hookFetching,
    refetch: hookRefetch,
    isLoading: hookLoading,
  } = useWorkList(params);

  // Prefer parent-supplied data when present
  const usingExternal = Array.isArray(itemsProp);
  const rawItems = usingExternal ? itemsProp : hookItems;
  const isFetching = usingExternal ? !!isFetchingProp : !!hookFetching;
  const isLoading = usingExternal ? !!isLoadingProp : !!hookLoading;
  const error = usingExternal ? errorProp : undefined;

  const unscheduled = useMemo(() => {
    const arr = Array.isArray(rawItems) ? rawItems : [];
    const filtered = arr.filter((w) => !w?.schedule?.start);
    if (!q.trim()) return filtered;
    const qq = q.trim().toLowerCase();
    return filtered.filter((w) => {
      const clientName = w?.clientSnapshot?.name || w?.sortKeys?.clientName || '';
      const hay = [
        w?.title,
        w?.description,
        Array.isArray(w?.tags) ? w.tags.join(' ') : '',
        w?.workNumber,
        clientName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(qq);
    });
  }, [rawItems, q]);

  const count = unscheduled.length;

  // refresh invalidation (hard) — only meaningful when we own the fetch
  const qc = useQueryClient();
  const hardRefresh = useCallback(async () => {
    if (typeof onRefresh === 'function') {
      await onRefresh();
      return;
    }
    if (usingExternal) return; // nothing to refresh locally
    await qc.invalidateQueries({ queryKey: WORK_KEYS.lists(params) });
    await hookRefetch();
  }, [onRefresh, usingExternal, qc, params, hookRefetch]);

  const toggleCollapsed = useCallback(() => {
    const next = !collapsed;
    if (controlled) onCollapsedChange?.(next);
    else setCollapsedState(next);
  }, [collapsed, controlled, onCollapsedChange]);

  // Build drag payload (custom MIME + JSON)
  const buildPayload = useCallback(
    (w) => {
      const m =
        w?.type === 'job'
          ? defaultDurations.job
          : w?.type === 'appointment'
          ? defaultDurations.appointment
          : defaultDurations.task;
      return JSON.stringify({
        kind: 'work',
        id: String(w?.id || w?._id),
        type: w?.type,
        title: w?.title,
        clientId: w?.clientSnapshot?.id ? String(w.clientSnapshot.id) : undefined,
        defaultDurationMinutes: Number.isFinite(m) ? m : 60,
      });
    },
    [defaultDurations]
  );

  const onDragStart = useCallback(
    (e, w) => {
      if (!dragEnabled) return;
      const payload = buildPayload(w);
      e.dataTransfer.setData('application/x-hypercrm-work', payload);
      e.dataTransfer.setData('application/json', payload); // helpful fallback
      e.dataTransfer.setData('text/plain', w?.title || 'Work');
      e.dataTransfer.effectAllowed = 'copyMove';
      setCleanDragImage(e.dataTransfer, w?.title, w?.type);
    },
    [buildPayload, dragEnabled]
  );

  // Quick action now ALWAYS opens details drawer (no EditWorkModal path)
  const openForSchedule = useCallback(
    (w) => {
      onOpenDetails?.(w);
    },
    [onOpenDetails]
  );

  // The scrollable list element doubles as the Draggable container
  const listRef = useRef(null);

  // Sticky header shadow on scroll
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onScroll = () => setScrolled(el.scrollTop > 0);
    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Register tray cards with FullCalendar Draggable so drops are accepted
  useEffect(() => {
    const el = listRef.current;
    if (!el || !dragEnabled) return;
    const draggable = new Draggable(el, {
      itemSelector: '[data-fc-draggable="1"]',
    });
    return () => draggable.destroy();
  }, [dragEnabled]);

  return (
    <aside
      className={cx(
        'flex w-full flex-col rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950',
        className
      )}
      aria-label="Unscheduled work tray"
      data-testid="unscheduled-tray"
    >
      {/* Sticky header */}
      <div
        className={cx(
          'sticky top-0 z-10 flex items-center justify-between gap-2 rounded-t-2xl bg-white/90 px-3 py-2 backdrop-blur dark:bg-slate-950/90',
          scrolled && 'shadow-sm'
        )}
      >
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-expanded={!collapsed}
            className="inline-flex size-8 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-slate-300 dark:hover:bg-slate-800"
            title={collapsed ? 'Expand' : 'Collapse'}
            data-testid="collapse-toggle"
          >
            <ChevronIcon className={cx('size-5 transition-transform', collapsed ? '-rotate-90' : 'rotate-90')} />
          </button>

          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            Unscheduled
            <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-200">
              {count}
            </span>
          </h3>
        </div>

        <button
          type="button"
          onClick={hardRefresh}
          className="inline-flex size-8 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-50 dark:text-slate-300 dark:hover:bg-slate-800"
          title="Refresh"
          aria-label="Refresh"
          disabled={isFetching || (usingExternal && !onRefresh)}
        >
          <RefreshIcon className={cx('size-5', isFetching && 'animate-spin')} />
        </button>
      </div>

      {/* collapsible body */}
      {!collapsed && (
        <>
          {/* Search */}
          <div className="px-3 pt-2">
            <label className="sr-only" htmlFor="unscheduled-search">
              Search unscheduled
            </label>
            <input
              id="unscheduled-search"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by title, tag, client, number…"
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
            />
          </div>

          {/* List */}
          <div
            ref={listRef}
            role="list"
            className="max-h-[70vh] overflow-y-auto px-3 pb-3 pt-2"
            aria-label="Unscheduled items"
          >
            {error ? (
              <div className="px-2 py-4 text-sm text-rose-600 dark:text-rose-400">Failed to load unscheduled items.</div>
            ) : unscheduled.length === 0 ? (
              <div className="px-2 py-10 text-center text-sm text-slate-500 dark:text-slate-400">
                {q ? 'No matches.' : isLoading ? 'Loading…' : 'Nothing to schedule right now.'}
              </div>
            ) : (
              unscheduled.map((w) => (
                <CardRow
                  key={String(w?.id || w?._id)}
                  work={w}
                  onOpenDetails={onOpenDetails}
                  onDragStart={onDragStart}
                  onQuickSchedule={openForSchedule}   
                  dragEnabled={dragEnabled}
                  defaultDurations={defaultDurations}
                />
              ))
            )}
          </div>
        </>
      )}
    </aside>
  );
}

UnscheduledTray.propTypes = {
  // Parent-controlled data (optional)
  items: PropTypes.array,
  isLoading: PropTypes.bool,
  isFetching: PropTypes.bool,
  error: PropTypes.any,
  onRefresh: PropTypes.func,

  // Actions
  onOpenDetails: PropTypes.func.isRequired,
  onCreateSchedule: PropTypes.func, // deprecated
  onScheduleNow: PropTypes.func,    // deprecated
  dragEnabled: PropTypes.bool,

  // Defaults & UI
  defaultDurations: PropTypes.shape({
    task: PropTypes.number,
    job: PropTypes.number,
    appointment: PropTypes.number,
  }),
  initialCollapsed: PropTypes.bool,
  collapsed: PropTypes.bool,
  onCollapsedChange: PropTypes.func,
  className: PropTypes.string,
};

/* -------------------------------- subviews -------------------------------- */

function CardRow({ work, onOpenDetails, onDragStart, onQuickSchedule, dragEnabled, defaultDurations }) {
  const id = String(work?.id || work?._id || '');
  const title = work?.title || '(untitled)';
  const type = work?.type || 'task';
  const clientName = work?.clientSnapshot?.name || work?.sortKeys?.clientName || null;
  const priority = work?.priority || 'normal';
  const workNumber = work?.workNumber || null;

  const scheduleDisabled = !dragEnabled;

  // tiny duration hint (teaches users what drop will do)
  const minutes =
    type === 'job' ? defaultDurations.job : type === 'appointment' ? defaultDurations.appointment : defaultDurations.task;

  return (
    <article
      role="listitem"
      tabIndex={0}
      data-fc-draggable="1"
      className="group mb-2 grid grid-cols-[6px_auto_min-content] items-center gap-2 rounded-xl border border-slate-200 bg-white px-2.5 py-2 shadow-sm outline-none ring-0 hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-indigo-500 dark:border-slate-800 dark:bg-slate-900 dark:hover:bg-slate-800 cursor-grab active:cursor-grabbing"
      draggable={!!dragEnabled}
      onDragStart={(e) => dragEnabled && onDragStart?.(e, work)}
      onClick={() => onOpenDetails?.(work)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onOpenDetails?.(work);
        } else if ((e.key === ' ' || e.key === 's' || e.key === 'S') && !scheduleDisabled) {
          e.preventDefault();
          onQuickSchedule?.(work);
        }
      }}
      data-work-id={id}
      data-testid={`unscheduled-card-${id}`}
      title={dragEnabled ? 'Drag to calendar to schedule • Enter: details • S: quick schedule' : 'View details'}
    >
      {/* left color bar by type */}
      <span className={cx('h-8 w-1.5 rounded-sm', TYPE_ACCENT[type] || TYPE_ACCENT.task)} aria-hidden="true" />

      {/* main content */}
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">{title}</h4>
          {workNumber ? (
            <span className="shrink-0 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              {workNumber}
            </span>
          ) : null}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
          <span className={cx('rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1', TYPE_PILL[type] || TYPE_PILL.task)}>
            {prettyType(type)}
          </span>
          <span className={cx('rounded-full px-2 py-0.5 text-[10px] font-medium', PRIORITY_BADGE[priority] || PRIORITY_BADGE.normal)}>
            {priority}{minutes ? ` • ${minutes}m` : ''}
          </span>
          {clientName ? (
            <span className="rounded-full bg-slate-50 px-2 py-0.5 text-[10px] font-medium text-slate-600 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-300 dark:ring-slate-700">
              {clientName}
            </span>
          ) : null}
        </div>
      </div>

      {/* actions (icon-only; appear on hover/focus) */}
      <div className="flex items-center gap-1 opacity-70 transition group-hover:opacity-100 group-focus-within:opacity-100">
        <button
          type="button"
          draggable={false}
          className={cx(
            'inline-flex size-8 items-center justify-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
            scheduleDisabled ? 'text-slate-400 cursor-not-allowed' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800'
          )}
          onClick={(e) => {
            e.stopPropagation();
            !scheduleDisabled && onQuickSchedule?.(work); // opens drawer
          }}
          title="Quick schedule"
          aria-label="Quick schedule"
          aria-disabled={scheduleDisabled}
          disabled={scheduleDisabled}
        >
          <CalendarPlusIcon className="size-4" />
        </button>
        <button
          type="button"
          draggable={false}
          className="inline-flex size-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-slate-300 dark:hover:bg-slate-800"
          title="Open details"
          aria-label="Open details"
          onClick={(e) => {
            e.stopPropagation();
            onOpenDetails?.(work);
          }}
        >
          <DotsIcon className="size-4" />
        </button>
      </div>
    </article>
  );
}

CardRow.propTypes = {
  work: PropTypes.object.isRequired,
  onOpenDetails: PropTypes.func,
  onDragStart: PropTypes.func,
  onQuickSchedule: PropTypes.func,
  dragEnabled: PropTypes.bool,
  defaultDurations: PropTypes.shape({
    task: PropTypes.number,
    job: PropTypes.number,
    appointment: PropTypes.number,
  }),
};
